import useUrlState from '@ahooksjs/use-url-state';
import { ProFormInstance } from '@ant-design/pro-form';
import { useLocalStorageState } from 'ahooks';
import moment from 'moment';
import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useAppConfig } from '@/config/context';
import { isAndroid } from '@/infrastructure/product-info';
import { PAGE_ID } from '@/infrastructure/reporter/datong/id';
import {
  CHARACTER_MATCH_ARRAY_OPTIONS,
  CHARACTER_MATCH_OPTIONS,
  VERSION_MATCH_OPTIONS,
} from '@/presentation/components/form/standards/types';
import { TimeQuickShortcut } from '@/presentation/components/form/time-quick-shortcut';
import { FilterEntry } from '@/presentation/components/form/types';
import { iconText } from '@/presentation/components/hint-text/hintText';
import {
  ANDROID_RESULT_CODE_TIPS, IOS_RESULT_CODE_TIPS,
  STATUS_CODE_TIPS,
  versionFilterTips,
} from '@/presentation/components/issue-list/components/tips';
import { ITdsFormRef, TdsFilterType, TdsForm, TdsFormItem, TdsFormItemType } from '@/presentation/components/tds-form';
import { DEFAULT_EVENT_TIME_RANGE } from '@/presentation/components/tds-form/config';
import { getInitTimeRange } from '@/presentation/components/tds-form/tds-date-cascader';
import {
  DISABLED_REPORT_TIME_OPTIONS,
  ISSUE_TIME_TYPE,
  ISSUE_TIME_TYPE_INCLUDES_MINITE_HOUR_DAY,
} from '@/presentation/components/tds-form/tds-date-cascader/config';
import { TdsColumnGroup } from '@/presentation/components/tds-form/type';
import {
  customFields2Filter,
  dateCascaderConverter,
  dateCascaderTransformer,
} from '@/presentation/components/tds-form/utils';
import { useTdsForm } from '@/presentation/hooks/form';
import { NetHttpProblemFieldKey, StandardNetHttpProblemFieldKey } from '@/presentation/pages/net/http/types';
import { CommonFieldKey, OptionType, StandardCommonFieldKey, Zone } from '@/proto/field';
import { DomainBelongType } from '@/proto/manage';
import { IFilter, TimeType } from '@/proto/metric';
import { genStandardToken, parseStandardToken } from '@/proto/token';
import { OWNERSHIP_LABEL } from './domain-ownership';


export const NetHttpProblemForm = (props: {
  onTokenChange?: (token: string) => void;
  name?: string;
  formRef?: any;
  entry?: FilterEntry;
}) => {
  const { t } = useTranslation();
  const formRef = useRef<ProFormInstance>();
  const tableRef = useRef<ITdsFormRef>(null);
  const [, useTdsFormContext] = useTdsForm;
  const [state, setState] = useUrlState({
    token: '',
  });
  const config = useAppConfig();
  const [timeRange, setTimeRange] = useLocalStorageState(
    `network-http-timeRange-${name}-v3`,
    {
      defaultValue: DEFAULT_EVENT_TIME_RANGE,
    },
  );

  const { updateFormContext } = useTdsFormContext();

  const customTimeQuickSetting = (timeType: TimeType): OptionType[] | undefined => {
    const optionsMap: any = {
      [TimeType.MINUTE5]: [
        {
          label: t('自定义'),
          value: TimeQuickShortcut.Custom,
        },
        {
          label: t('近1小时'),
          value: TimeQuickShortcut.Last1Hour,
        },
        {
          label: t('今天'),
          value: TimeQuickShortcut.Today,
        },
        {
          label: t('昨天'),
          value: TimeQuickShortcut.Yesterday,
        },
      ],
      [TimeType.HOUR]: [
        {
          label: t('自定义'),
          value: TimeQuickShortcut.Custom,
        },
        {
          label: t('近1小时'),
          value: TimeQuickShortcut.Last1Hour,
        },
        {
          label: t('今天'),
          value: TimeQuickShortcut.Today,
        },
        {
          label: t('昨天'),
          value: TimeQuickShortcut.Yesterday,
        },
      ],
      [TimeType.DAY]: [
        {
          label: t('自定义'),
          value: TimeQuickShortcut.Custom,
        },
        {
          label: t('近7天'),
          value: TimeQuickShortcut.Last7Days,
        },
      ],
    };

    return optionsMap[timeType] || optionsMap[TimeType.DAY];
  };

  const items: TdsFormItem[] = [
    {
      type: TdsFormItemType.DateCascader,
      label: t('时间范围'),
      name: StandardCommonFieldKey.TimeRange,
      width: 462,
      deleteable: false,
      convertValue: dateCascaderConverter,
      transform: dateCascaderTransformer,
      fieldProps: {
        // @ts-ignore - limitedDays 对象不需要包含所有 TimeType 值
        limitedDays: props.entry === FilterEntry.NetHttpDetail
          ? {
            [TimeType.HOUR]: 6,
            [TimeType.DAY]: 6,
            [TimeType.MINUTE5]: 1,
            [TimeType.WEEK]: 6,
            [TimeType.MONTH]: 6,
            [TimeType.NULL]: 6,
            [TimeType.SECOND]: 6,
            [TimeType.MINUTE]: 6,
            [TimeType.ACCUMULATED]: 6,
          } : undefined,
        maxLimitedDays: 90,
        timeWindowOptions:
          props.entry === FilterEntry.NetHttpDetail
            ? ISSUE_TIME_TYPE_INCLUDES_MINITE_HOUR_DAY
            : ISSUE_TIME_TYPE,
        timeTypeOptions: DISABLED_REPORT_TIME_OPTIONS,
        customTimeQuickSetting:
          props.entry === FilterEntry.NetHttpDetail
            ? customTimeQuickSetting
            : undefined,
      },
      inFilter: true,
      filterType: TdsFilterType.DateCascader,
    },
    {
      type: TdsFormItemType.CharacterInput,
      labelTools: [iconText(versionFilterTips)],
      label: t('APP版本'),
      labelString: t('APP版本'),
      name: StandardCommonFieldKey.AppVersion,
      deleteable: false,
      width: 360,
      inFilter: true,
      filterType: TdsFilterType.Version,
      filterName: CommonFieldKey.AppVersion,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.AppVersion,
          fieldScope: Zone.Network,
        },
        modeSelectProps: {
          style: {
            width: 106,
          },
        },
        modeOptions: VERSION_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('系统版本'),
      name: StandardCommonFieldKey.FullOsVersion,
      group: TdsColumnGroup.DeviceInfo,
      filterType: TdsFilterType.Version,
      filterName: CommonFieldKey.OsVersion,
      inFilter: true,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.OsVersion,
          fieldScope: Zone.Network,
        },
        mode: 'tags',
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('厂商'),
      group: TdsColumnGroup.DeviceInfo,
      name: StandardCommonFieldKey.Brand,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Brand,
          fieldScope: Zone.Network,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.Brand,
      filterType: TdsFilterType.String,
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('场景'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardCommonFieldKey.Stage,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Stage,
          fieldScope: Zone.Network,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      inFilter: true,
      filterName: CommonFieldKey.Stage,
      filterType: TdsFilterType.String,
      defaultEditHidden: true,
    },
    {
      defaultEditHidden: true,
      type: TdsFormItemType.CharacterInput,
      label: t('机型'),
      group: TdsColumnGroup.DeviceInfo,
      name: StandardCommonFieldKey.Model,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: CommonFieldKey.Model,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: CommonFieldKey.Model,
          fieldScope: Zone.Network,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('SDK版本'),
      group: TdsColumnGroup.ApplicationInfo,
      name: StandardCommonFieldKey.SdkVersion,
      filterName: CommonFieldKey.SdkVersion,
      inFilter: true,
      filterType: TdsFilterType.Version,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.SdkVersion,
          fieldScope: Zone.Network,
        },
        mode: 'tags',
        modeOptions: VERSION_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('进程'),
      group: TdsColumnGroup.exceptionInfo,
      name: StandardCommonFieldKey.ProcessName,
      filterName: CommonFieldKey.ProcessName,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('设备ID'),
      group: TdsColumnGroup.DeviceInfo,
      name: StandardCommonFieldKey.Qimei,
      filterName: CommonFieldKey.Qimei,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('用户ID'),
      group: TdsColumnGroup.UserInfo,
      name: StandardCommonFieldKey.AccountId,
      filterName: CommonFieldKey.AccountId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'Bundle ID',
      group: TdsColumnGroup.ApplicationInfo,
      name: StandardCommonFieldKey.BundleId,
      filterName: CommonFieldKey.BundleId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.BundleId,
          fieldScope: Zone.Network,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('启动ID'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardCommonFieldKey.LaunchId,
      filterName: CommonFieldKey.LaunchId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('进程启动ID'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardCommonFieldKey.ProcessLaunchId,
      filterName: CommonFieldKey.ProcessLaunchId,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('实验ID'),
      group: TdsColumnGroup.Custom,
      name: StandardCommonFieldKey.ShiplyTag,
      filterName: CommonFieldKey.ShiplyTag,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(CommonFieldKey.ShiplyAbTestTag, fieldValue, 'a11'),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.ShiplyTag,
          fieldScope: Zone.Network,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
      hidden: !config.enableShiplyAbTestTag,
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('个例标签'),
      group: TdsColumnGroup.Tag,
      name: StandardCommonFieldKey.TagsInFilter,
      filterName: CommonFieldKey.TagsInFilter,
      inFilter: true,
      filterType: TdsFilterType.Custom,
      filterTransform: (_fieldKey, fieldValue) => customFields2Filter(CommonFieldKey.TagsInFilter, fieldValue, 'a13'),
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.TagsInFilter,
          fieldScope: Zone.Network,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_ARRAY_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.FieldSelector,
      label: t('运营商'),
      group: TdsColumnGroup.UserInfo,
      name: StandardNetHttpProblemFieldKey.ClientOperator,
      filterName: NetHttpProblemFieldKey.ClientOperator,
      inFilter: true,
      filterType: TdsFilterType.SingleString,
      fieldProps: {
        fieldKey: NetHttpProblemFieldKey.ClientOperator,
        fieldScope: Zone.Network,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: 'URL',
      group: TdsColumnGroup.ContextInfo,
      name: StandardNetHttpProblemFieldKey.URL,
      filterName: NetHttpProblemFieldKey.URL,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
        valueSelectProps: {
          fieldKey: NetHttpProblemFieldKey.URL,
          fieldScope: Zone.Network,
        },
      },
      defaultEditHidden: true,
      disabled: props.entry !== FilterEntry.NetHttpDomainDetail,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('域名'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardNetHttpProblemFieldKey.Host,
      filterName: NetHttpProblemFieldKey.Host,
      inFilter: true,
      filterType: TdsFilterType.String,
      disabled: props.entry === FilterEntry.NetHttpDomainDetail,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
        valueSelectProps: {
          fieldKey: NetHttpProblemFieldKey.Host,
          fieldScope: Zone.Network,
        },
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.FieldSelector,
      label: '域名归属',
      group: TdsColumnGroup.ContextInfo,
      name: StandardNetHttpProblemFieldKey.DomainOwnership,
      filterName: NetHttpProblemFieldKey.DomainOwnership,
      inFilter: true,
      filterType: TdsFilterType.SingleString,
      fieldProps: {
        options: [
          {
            label: OWNERSHIP_LABEL[DomainBelongType.DOMAIN_BELONG_ALL],
            value: String(DomainBelongType.DOMAIN_BELONG_ALL),
          },
          {
            label: OWNERSHIP_LABEL[DomainBelongType.DOMAIN_BELONG_SELF],
            value: String(DomainBelongType.DOMAIN_BELONG_SELF),
          },
          {
            label: OWNERSHIP_LABEL[DomainBelongType.DOMAIN_BELONG_THIRD],
            value: String(DomainBelongType.DOMAIN_BELONG_THIRD),
          },
          {
            label: OWNERSHIP_LABEL[DomainBelongType.DOMAIN_BELONG_UNCLASSIFIED],
            value: String(DomainBelongType.DOMAIN_BELONG_UNCLASSIFIED),
          },
        ],
      },
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('接口'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardNetHttpProblemFieldKey.Path,
      filterName: NetHttpProblemFieldKey.Path,
      inFilter: true,
      filterType: TdsFilterType.String,
      disabled: props.entry === FilterEntry.NetHttpDomainDetail,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
        valueSelectProps: {
          fieldKey: NetHttpProblemFieldKey.Path,
          fieldScope: Zone.Network,
        },
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.FieldSelector,
      group: TdsColumnGroup.exceptionInfo,
      labelTools: [iconText(isAndroid() ? ANDROID_RESULT_CODE_TIPS : IOS_RESULT_CODE_TIPS)],
      label: t('结果码'),
      labelString: t('结果码'),
      name: StandardNetHttpProblemFieldKey.ResultCode,
      filterName: NetHttpProblemFieldKey.ResultCode,
      inFilter: true,
      filterType: TdsFilterType.EnumString,
      fieldProps: {
        mode: 'multiple',
        fieldKey: NetHttpProblemFieldKey.ResultCode,
        fieldScope: Zone.Network,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.FieldSelector,
      label: t('HTTP请求方法'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardNetHttpProblemFieldKey.Method,
      inFilter: true,
      filterType: TdsFilterType.SingleString,
      filterName: NetHttpProblemFieldKey.Method,
      fieldProps: {
        fieldKey: NetHttpProblemFieldKey.Method,
        fieldScope: Zone.Network,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.FieldSelector,
      group: TdsColumnGroup.exceptionInfo,
      labelTools: [iconText(STATUS_CODE_TIPS)],
      label: t('HTTP状态码'),
      labelString: t('HTTP状态码'),
      name: StandardNetHttpProblemFieldKey.StatusCode,
      filterName: NetHttpProblemFieldKey.StatusCode,
      inFilter: true,
      filterType: TdsFilterType.EnumString,
      fieldProps: {
        mode: 'tags',
        fieldKey: NetHttpProblemFieldKey.StatusCode,
        fieldScope: Zone.Network,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('服务器IP'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardNetHttpProblemFieldKey.HostIp,
      filterName: NetHttpProblemFieldKey.HostIp,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
        valueSelectProps: {
          fieldKey: NetHttpProblemFieldKey.HostIp,
          fieldScope: Zone.Network,
        },
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('服务器国家'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardNetHttpProblemFieldKey.HostCountry,
      filterName: NetHttpProblemFieldKey.HostCountry,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: NetHttpProblemFieldKey.HostCountry,
          fieldScope: Zone.Network,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('服务器省份'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardNetHttpProblemFieldKey.HostProvince,
      filterName: NetHttpProblemFieldKey.HostProvince,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: NetHttpProblemFieldKey.HostProvince,
          fieldScope: Zone.Network,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('服务器城市'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardNetHttpProblemFieldKey.HostCity,
      filterName: NetHttpProblemFieldKey.HostCity,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: NetHttpProblemFieldKey.HostCity,
          fieldScope: Zone.Network,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('服务器运营商'),
      group: TdsColumnGroup.ContextInfo,
      name: StandardNetHttpProblemFieldKey.HostOperator,
      inFilter: true,
      filterType: TdsFilterType.String,
      filterName: NetHttpProblemFieldKey.HostOperator,
      fieldProps: {
        mode: 'tags',
        valueSelectProps: {
          fieldKey: NetHttpProblemFieldKey.HostOperator,
          fieldScope: Zone.Network,
        },
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.NumberInput,
      label: t('请求耗时'),
      group: TdsColumnGroup.exceptionInfo,
      name: StandardNetHttpProblemFieldKey.TotalCostInMs,
      filterName: NetHttpProblemFieldKey.TotalCostInMs,
      inFilter: true,
      filterType: TdsFilterType.Number,
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.NumberInput,
      label: t('DNS时间'),
      group: TdsColumnGroup.exceptionInfo,
      name: NetHttpProblemFieldKey.DnsCostInMs,
      filterName: NetHttpProblemFieldKey.DnsCostInMs,
      inFilter: true,
      filterType: TdsFilterType.Number,
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.NumberInput,
      label: t('TCP时间'),
      group: TdsColumnGroup.exceptionInfo,
      name: StandardNetHttpProblemFieldKey.TcpCostInMs,
      filterName: NetHttpProblemFieldKey.TcpCostInMs,
      inFilter: true,
      filterType: TdsFilterType.Number,
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.NumberInput,
      label: t('SSL时间'),
      group: TdsColumnGroup.exceptionInfo,
      name: StandardNetHttpProblemFieldKey.SslCostInMs,
      filterName: NetHttpProblemFieldKey.SslCostInMs,
      inFilter: true,
      filterType: TdsFilterType.Number,
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.NumberInput,
      label: t('发送时间'),
      group: TdsColumnGroup.exceptionInfo,
      name: StandardNetHttpProblemFieldKey.RequestCostInMs,
      filterName: NetHttpProblemFieldKey.RequestCostInMs,
      inFilter: true,
      filterType: TdsFilterType.Number,
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.NumberInput,
      label: t('等待时间'),
      group: TdsColumnGroup.exceptionInfo,
      name: StandardNetHttpProblemFieldKey.WaitCostInMs,
      filterName: NetHttpProblemFieldKey.WaitCostInMs,
      inFilter: true,
      filterType: TdsFilterType.Number,
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.NumberInput,
      label: t('接收时间'),
      group: TdsColumnGroup.exceptionInfo,
      name: StandardNetHttpProblemFieldKey.ResponseCostInMs,
      filterName: NetHttpProblemFieldKey.ResponseCostInMs,
      inFilter: true,
      filterType: TdsFilterType.Number,
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.NumberInput,
      label: t('请求字节数'),
      group: TdsColumnGroup.exceptionInfo,
      name: StandardNetHttpProblemFieldKey.RequestByteCount,
      filterName: NetHttpProblemFieldKey.RequestByteCount,
      inFilter: true,
      filterType: TdsFilterType.Number,
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.NumberInput,
      label: t('接收字节数'),
      group: TdsColumnGroup.exceptionInfo,
      name: StandardNetHttpProblemFieldKey.ResponseByteCount,
      filterName: NetHttpProblemFieldKey.ResponseByteCount,
      inFilter: true,
      filterType: TdsFilterType.Number,
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('异常类型'),
      group: TdsColumnGroup.exceptionInfo,
      name: StandardCommonFieldKey.BasicExceptionType,
      filterName: CommonFieldKey.BasicExceptionType,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        valueSelectProps: {
          fieldKey: CommonFieldKey.BasicExceptionType,
          fieldScope: Zone.Network,
        },
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
      },
      defaultEditHidden: true,
    },
    {
      type: TdsFormItemType.CharacterInput,
      label: t('异常信息'),
      group: TdsColumnGroup.exceptionInfo,
      name: CommonFieldKey.ExceptionType,
      inFilter: true,
      filterType: TdsFilterType.String,
      fieldProps: {
        mode: 'tags',
        modeOptions: CHARACTER_MATCH_OPTIONS,
        fieldKey: CommonFieldKey.ExceptionType,
        fieldScope: Zone.Network,
      },
      defaultEditHidden: true,
    },
  ];

  const initToken = async () => {
    if (!state.token) {
      formRef.current?.submit();
    } else {
      const fieldsValue = await parseStandardToken(state.token);
      // 链接有token时，强制展示有value值的表单项
      tableRef?.current?.showFormItem?.(items, fieldsValue)?.then?.(() => {
        formRef?.current?.setFieldsValue?.(fieldsValue);
        if (
          props.entry === FilterEntry.NetHttpDetail && (fieldsValue.time_window_type === 5
          || fieldsValue.time_window_type === 6
          || fieldsValue.short_cut === '近90天'
          || moment.unix(fieldsValue.date_value[1]).diff(moment.unix(fieldsValue.date_value[0]), 'days') > 7)
        ) {
          formRef?.current?.setFieldsValue?.({
            standard_time_range: {
              ...fieldsValue.standard_time_range,
              time_window_type: 1,
              short_cut: '近7天',
            },
          });
        }
      });
      updateFormContext((draft) => {
        // eslint-disable-next-line no-param-reassign
        draft.fieldsValue = fieldsValue;
        // eslint-disable-next-line no-param-reassign
        draft.fieldsFilter = fieldsValue?.filters ?? [];
      });

      props.onTokenChange?.(state.token);
    }
  };

  const onFinish = async (values: Record<string, any>, filters: IFilter[]) => {
    // 缓存选择过的时间范围
    setTimeRange(values?.[StandardCommonFieldKey.TimeRange] ?? DEFAULT_EVENT_TIME_RANGE);
    const token = await genStandardToken({
      ...values,
      filters,
    });

    updateFormContext((draft) => {
      // eslint-disable-next-line no-param-reassign
      draft.token = token;
      // eslint-disable-next-line no-param-reassign
      draft.fieldsValue = values;
      // eslint-disable-next-line no-param-reassign
      draft.fieldsFilter = filters;
    });

    setState({
      token,
    });

    props.onTokenChange?.(token);
  };

  useEffect(() => {
    initToken();
  }, []);
  const initialTimeRange = useMemo(() => (timeRange ? {
    ...timeRange,
    date_value: getInitTimeRange(timeRange.time_window_type!, timeRange.short_cut!),
  } : timeRange), [timeRange]);

  return <TdsForm
    ref={tableRef}
    pageId={PAGE_ID.network.list}
    formRef={props.formRef || formRef}
    onFinish={onFinish}
    items={items}
    name={props.name || 'net-list'}
    initialValues={{
      [StandardCommonFieldKey.TimeRange]: initialTimeRange,
    }}
    renderByGroup
  />;
};
